import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowRight, Package, Truck, Globe, Database, Brain, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const Solutions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const solutions = [
    {
      id: 1,
      icon: Package,
      title: t('solutions.importExport.title'),
      subtitle: t('solutions.importExport.subtitle'),
      description: t('solutions.importExport.description'),
      image: 'https://images.unsplash.com/photo-1679183958890-e8f1beb51e8a?q=80&w=2070',
      features: [
        t('solutions.importExport.features.certification'),
        t('solutions.importExport.features.compliance'),
        t('solutions.importExport.features.bulk')
      ],
      route: '/solutions/import-export'
    },
    {
      id: 2,
      icon: Truck,
      title: t('solutions.distribution.title'),
      subtitle: t('solutions.distribution.subtitle'),
      description: t('solutions.distribution.description'),
      image: 'https://images.unsplash.com/photo-1513828742140-ccaa28f3eda0?q=80&w=2070',
      features: [
        t('solutions.distribution.features.delivery'),
        t('solutions.distribution.features.inventory'),
        t('solutions.distribution.features.support')
      ],
      route: '/solutions/distribution'
    },
    {
      id: 3,
      icon: Globe,
      title: t('solutions.b2bPlatform.title'),
      subtitle: t('solutions.b2bPlatform.subtitle'),
      description: t('solutions.b2bPlatform.description'),
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070',
      features: [
        t('solutions.b2bPlatform.features.pricing'),
        t('solutions.b2bPlatform.features.tracking'),
        t('solutions.b2bPlatform.features.documentation')
      ],
      route: '/solutions/b2b-platform'
    },
    {
      id: 4,
      icon: Database,
      title: t('solutions.logistics.title'),
      subtitle: t('solutions.logistics.subtitle'),
      description: t('solutions.logistics.description'),
      image: 'https://images.unsplash.com/photo-1713859288954-3314e2fcaa7a?q=80&w=2070',
      features: [
        t('solutions.logistics.features.customs'),
        t('solutions.logistics.features.insurance'),
        t('solutions.logistics.features.tracking')
      ],
      route: '/solutions/logistics'
    },
    {
      id: 5,
      icon: Brain,
      title: t('solutions.smartBuilding.title'),
      subtitle: t('solutions.smartBuilding.subtitle'),
      description: t('solutions.smartBuilding.description'),
      image: 'https://images.unsplash.com/photo-1545034972-8429e5efe564?q=80&w=2070',
      features: [
        t('solutions.smartBuilding.features.analytics'),
        t('solutions.smartBuilding.features.optimization'),
        t('solutions.smartBuilding.features.sustainability')
      ],
      route: '/solutions/smart-building'
    }
  ];

  return (
    <section id="solutions" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
            {t('solutions.title')}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {t('solutions.subtitle')}
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="space-y-12">
          {solutions.map((solution, index) => {
            const IconComponent = solution.icon;
            const isReverse = index % 2 === 1;
            
            return (
              <Card key={solution.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className={`grid lg:grid-cols-2 gap-0 ${isReverse ? 'lg:grid-flow-col-dense' : ''}`}>
                  {/* Content */}
                  <CardContent className={`p-12 flex flex-col justify-center ${isReverse ? 'lg:col-start-2' : ''}`}>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                        <IconComponent size={24} className="text-gray-700" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-medium text-gray-900">{solution.title}</h3>
                        <p className="text-gray-600">{solution.subtitle}</p>
                      </div>
                    </div>
                    
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                      {solution.description}
                    </p>
                    
                    <ul className="space-y-2 mb-8">
                      {solution.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-gray-600">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      onClick={() => navigate(solution.route)}
                      className="self-start bg-gray-900 hover:bg-gray-800 text-white"
                    >
                      {t('solutions.learnMore')}
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </CardContent>
                  
                  {/* Image */}
                  <div className={`relative ${isReverse ? 'lg:col-start-1' : ''}`}>
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-64 lg:h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10"></div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Solutions;